import { useState } from 'react';
import type { ReactNode } from 'react';
import { useApi, useAction, useRead } from '../../context';
import { Failure, Field } from '../../bits';
import { Switch } from '../../kit';
import { useT } from '../../../messages';
import type { T } from '../../../messages';
import type { CatalogImage, CatalogItem } from '../../../wire';
import { textOf } from './text';

interface PhotoDraft {
  url: string;
  alt: string;
  /** On their website (B10). A new photograph is. */
  onWebsite: boolean;
}

/** What the console's upload route accepts, restated for the sentence before the trip. */
const UPLOADABLE = ['image/jpeg', 'image/png', 'image/webp'];
/** 10 MB — `CATALOG_UPLOAD_MAX_BYTES` on `admin-api`, which stays the fence. */
const MAX_BYTES = 10_485_760;

/**
 * Why a chosen file will not be sent, or `null` when it may be.
 *
 * The server refuses the same three things with its own sentences; this is the
 * sentence before the trip, so an operator who picks an iPhone's HEIC reads
 * what to do while the file is still on their desk. HEIC is caught by name as
 * well as by type, because some browsers hand it over with no type at all.
 */
function refusalOf(t: T, file: File): string | null {
  const name = file.name.toLowerCase();
  if (
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  ) {
    return t('catalog.upload.heic', { file: file.name });
  }
  if (!UPLOADABLE.includes(file.type)) return t('catalog.upload.notPicture', { file: file.name });
  if (file.size > MAX_BYTES) return t('catalog.upload.tooBig', { file: file.name });
  return null;
}

/**
 * An item's photographs: upload from this computer, pick one of the owner's
 * own, or paste an address; then say what each shows, order them, and save.
 *
 * The set is edited locally and sent **whole**, which is what `repos/media.ts`
 * does and why — "the array is the statement of which photographs this item
 * has", positions `0..n-1`, so a reorder is not four renumbering writes.
 *
 * **An upload attaches nothing by itself.** It puts the file where a page can
 * show it and adds its address to this list, like a pasted one; **Save
 * photographs** is the one write that hangs any of them on the item. So an
 * upload abandoned half-way leaves the item exactly as it was.
 *
 * **Alt text is required before Save.** A photograph of a thing on sale is never
 * decorative: the guest who cannot see it is the guest being told what they are
 * buying. An uploaded file's box opens empty — a file name is not a sentence.
 */
export function ItemPhotos({
  tenantId,
  item,
  locale,
  uploadsConfigured,
  onChanged,
}: {
  tenantId: string;
  item: CatalogItem;
  locale: string;
  /** Both buckets and the public address are set on this deployment. */
  uploadsConfigured: boolean;
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const upload = useAction();
  const [images, setImages] = useState<PhotoDraft[]>(() =>
    item.images.map((i: CatalogImage) => ({
      url: i.url,
      alt: textOf(i.alt, locale),
      onWebsite: i.on_website,
    })),
  );
  const [url, setUrl] = useState('');
  const [saved, setSaved] = useState(false);
  const [refused, setRefused] = useState<string[]>([]);
  const [progress, setProgress] = useState<{ done: number; of: number } | null>(null);

  /**
   * The owner's own photographs, by slot. `service:<item id>` is the shot
   * list's per-item slot, so a picture taken *of this treatment* is offered
   * first; everything else they sent is offered underneath. A business with no
   * intake answers `404` and the picker simply is not drawn.
   */
  const uploads = useRead(() => api.intakeUploads(tenantId), [tenantId]);
  const promoted = (uploads.value?.uploads ?? []).filter(
    (u) => u.status === 'promoted' && u.promoted_url !== null,
  );
  const mine = promoted.filter((u) => u.slot === `service:${item.item_id}`);
  const rest = promoted.filter((u) => u.slot !== `service:${item.item_id}`);

  const changed = (): void => {
    action.clear();
    setSaved(false);
  };

  const add = (next: string, alt = ''): void => {
    if (next.trim() === '') return;
    changed();
    setImages((was) =>
      was.some((i) => i.url === next) ? was : [...was, { url: next, alt, onWebsite: true }],
    );
  };

  const move = (index: number, by: number): void => {
    const to = index + by;
    if (to < 0 || to >= images.length) return;
    const next = [...images];
    const [moved] = next.splice(index, 1);
    next.splice(to, 0, moved as PhotoDraft);
    changed();
    setImages(next);
  };

  /** One file at a time, in the order chosen: a failure names where it stopped. */
  const uploadFiles = async (files: readonly File[]): Promise<void> => {
    const refusals: string[] = [];
    const accepted: File[] = [];
    for (const file of files) {
      const why = refusalOf(t, file);
      if (why === null) accepted.push(file);
      else refusals.push(why);
    }
    setRefused(refusals);
    if (accepted.length === 0) return;
    await upload.run(async () => {
      for (const [index, file] of accepted.entries()) {
        setProgress({ done: index, of: accepted.length });
        const address = await api.uploadCatalogPhoto(tenantId, item.item_id, file);
        add(address);
      }
    });
    setProgress(null);
  };

  const missingAlt = images.some((i) => i.alt.trim() === '');

  const save = async (): Promise<void> => {
    const ok = await action.run(async () => {
      await api.setCatalogMedia(
        tenantId,
        item.item_id,
        images.map((i) => ({ url: i.url, alt: { [locale]: i.alt }, on_website: i.onWebsite })),
      );
    });
    if (ok) {
      setSaved(true);
      onChanged();
    }
  };

  return (
    <div className="catalog-block" data-photos={item.item_id}>
      {images.length === 0 ? (
        <p className="muted">{t('catalog.photosEmpty')}</p>
      ) : (
        <ol className="catalog-photos">
          {images.map((image, index) => (
            <li key={image.url} className="catalog-photo">
              <img className="catalog-photo__thumb" src={image.url} alt="" loading="lazy" />
              <div className="catalog-photo__body">
                <Field label={t('catalog.alt')} hint={t('catalog.photos.altHint')}>
                  <input
                    aria-label={t('catalog.altOf', { position: index + 1 })}
                    value={image.alt}
                    aria-invalid={image.alt.trim() === ''}
                    onChange={(e) => {
                      const next = [...images];
                      next[index] = { ...image, alt: e.target.value };
                      changed();
                      setImages(next);
                    }}
                  />
                </Field>
                {/*
                 * B10: every photograph stays in the list; the switch decides
                 * whether their website shows it. Sent with the whole set on
                 * Save, like the order and the words.
                 */}
                <div className="catalog-photo__site">
                  <Switch
                    checked={image.onWebsite}
                    label={t('catalog.website.photoOf', { position: index + 1 })}
                    onChange={(on) => {
                      const next = [...images];
                      next[index] = { ...image, onWebsite: on };
                      changed();
                      setImages(next);
                    }}
                  />
                  <span className={image.onWebsite ? 'muted' : 'catalog-photo__off'}>
                    {image.onWebsite ? t('catalog.website.photo') : t('catalog.website.off')}
                  </span>
                </div>
                <div className="row-actions">
                  <button
                    type="button"
                    className="button button--quiet button--sm"
                    aria-label={t('catalog.moveUp', { position: index + 1 })}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="button button--quiet button--sm"
                    aria-label={t('catalog.moveDown', { position: index + 1 })}
                    disabled={index === images.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="button button--quiet button--sm"
                    aria-label={t('catalog.removePhoto', { position: index + 1 })}
                    onClick={() => {
                      changed();
                      setImages(images.filter((_, i) => i !== index));
                    }}
                  >
                    {t('catalog.remove')}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      <div className="catalog-photo-sources">
        {uploadsConfigured ? (
          <Field label={t('catalog.upload.label')} hint={t('catalog.upload.hint')}>
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              aria-label={t('catalog.upload.label')}
              disabled={upload.busy}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                // Cleared so choosing the same file again (after fixing it)
                // fires another change.
                e.target.value = '';
                void uploadFiles(files);
              }}
            />
          </Field>
        ) : (
          <p className="muted">{t('catalog.upload.off')}</p>
        )}

        {promoted.length === 0 ? null : (
          <Field label={t('catalog.photos.fromIntake')} hint={t('catalog.photos.fromIntakeHint')}>
            <select
              aria-label={t('catalog.photos.fromIntake')}
              value=""
              onChange={(e) => {
                const picked = promoted.find((u) => u.promoted_url === e.target.value);
                // The owner's own file name is the only words anybody has
                // written about this picture yet, so it seeds the alt box — and
                // the box still wants a sentence before a guest reads it.
                add(e.target.value, picked?.original_name ?? '');
              }}
            >
              <option value="">{t('catalog.pick')}</option>
              {mine.length === 0 ? null : (
                <optgroup label={t('catalog.forThisItem')}>
                  {mine.map((u) => (
                    <option key={u.upload_id} value={u.promoted_url ?? ''}>
                      {u.original_name || u.upload_id}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label={t('catalog.photos.otherPhotos')}>
                {rest.map((u) => (
                  <option key={u.upload_id} value={u.promoted_url ?? ''}>
                    {u.slot} — {u.original_name || u.upload_id}
                  </option>
                ))}
              </optgroup>
            </select>
          </Field>
        )}

        <div className="catalog-paste">
          <Field label={t('catalog.pasteUrl')} hint={t('catalog.photos.pasteHint')}>
            <input
              aria-label={t('catalog.pasteUrl')}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </Field>
          <button
            type="button"
            className="button button--sm"
            disabled={url.trim() === ''}
            onClick={() => {
              add(url.trim());
              setUrl('');
            }}
          >
            {t('catalog.addPhoto')}
          </button>
        </div>
      </div>

      {progress === null ? null : (
        <p className="muted" role="status">
          {t('catalog.upload.progress', { done: progress.done + 1, of: progress.of })}
        </p>
      )}
      {refused.map((sentence) => (
        <p key={sentence} className="catalog-fence-note" role="alert">
          {sentence}
        </p>
      ))}
      {upload.failure !== null ? <Failure failure={upload.failure} /> : null}

      <div className="row-actions">
        <button
          type="button"
          className="button button--primary button--sm"
          disabled={missingAlt || action.busy || upload.busy}
          onClick={() => void save()}
        >
          {action.busy ? t('common.saving') : t('catalog.savePhotos')}
        </button>
      </div>

      {missingAlt ? <p className="catalog-fence-note">{t('catalog.altRequired')}</p> : null}
      {action.failure !== null ? <Failure failure={action.failure} /> : null}
      {saved ? <p className="ok">{t('catalog.photosSaved')}</p> : null}
    </div>
  );
}
